import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { from, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, toArray } from "rxjs/operators";

import { RegionActions } from "../region/region.actions";
import { MetricsActions } from "./metrics.actions";
import { MetricsService } from "./metrics.service";

@Injectable()
export class MetricsEffects {
  private actions$ = inject(Actions);
  private metricsService = inject(MetricsService);
  private store = inject(Store);

  loadMetrics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetricsActions.loadMetrics),
      switchMap(({ requests }) => {
        if (requests.length === 0) {
          return of(
            MetricsActions.loadMetricsRawData({
              regionMetrics: [],
            })
          );
        }

        const requestedRegions = requests.map(req => req.region);

        return from(requests).pipe(
          mergeMap(request =>
            this.metricsService.getMetrics(request).pipe(
              map(metricsArray => ({
                region: request.region,
                metrics: metricsArray,
                success: true,
              })),
              catchError(() =>
                of({
                  region: request.region,
                  metrics: [],
                  success: false,
                })
              )
            )
          ),
          toArray(),
          map(results => {
            const successfulResults = results.filter(r => r.success);
            const failedRegions = results
              .filter(r => !r.success && r.region)
              .map(r => r.region as string);
            const successfulRegions = results
              .filter(r => r.success && r.region)
              .map(r => r.region as string);

            if (failedRegions.length > 0) {
              const errorMessage = `Failed to load metrics from: ${failedRegions.join(
                ", "
              )}`;

              this.store.dispatch(
                MetricsActions.setErrorState({
                  error: errorMessage,
                  failedRegions,
                })
              );

              if (successfulResults.length > 0) {
                if (successfulRegions.length < requestedRegions.length) {
                  this.store.dispatch(RegionActions.clearSelectedRegions());

                  successfulRegions.forEach(region => {
                    this.store.dispatch(
                      RegionActions.toggleSelectedRegion({ region })
                    );
                  });
                }

                return MetricsActions.loadMetricsRawData({
                  regionMetrics: successfulResults.map(result => ({
                    region: result.region as string,
                    metrics: result.metrics,
                  })),
                });
              } else {
                return MetricsActions.loadMetricsRawData({
                  regionMetrics: [],
                });
              }
            }

            return MetricsActions.loadMetricsRawData({
              regionMetrics: successfulResults.map(result => ({
                region: result.region as string,
                metrics: result.metrics,
              })),
            });
          })
        );
      })
    )
  );
}
